import { Component, createRef } from 'preact';
import { createPortal } from 'preact/compat';
import { fetchMessages, sendChatMessage, claimChatMessage, markChatRead } from '../utils/arena/chatApi';
import { fetchReplaySummary } from '../utils/game/replay/replayApi';
import { refreshMailbox } from '../utils/presenceManager';
import {
  getCachedMessages, saveCachedMessages, mergeMessages,
} from '../utils/arena/chatCache';
import { CoinIcon } from './ui/icons';
import { declineDraftEvent } from '../utils/arena/draftApi';
import { CloseButton, ForwardButton } from './ui/navButtons';
import RuneSpinner from './RuneSpinner';
import { playUI, UI } from '../utils/arena/uiSounds';
import { ART_GROUP_BASE, printingsInArtGroup, artGroupDisplayName } from '../utils/sorcery/artGroups';
import CardInspector from './CardInspector';
import CardHoverPreview from './CardHoverPreview';
import CardMentionText from './CardMentionText';
import PlayerTitle from './PlayerTitle';
import { rectToCanvasRect } from '../utils/cardMentions';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, COIN_COLOR,
  OPAQUE_SUB_PANEL_STYLE, OPAQUE_TEXTURED_PANEL_STYLE,
  PANEL_BG, INPUT_STYLE, DANGER_BTN,
  panelCornerStyle, panelCornerGutter,
} from '../lib/medievalTheme';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import ReplayShareCard, { replayMetaFromHistory } from './ReplayShareCard';
import DraftInviteCard from './DraftInviteCard';
import TavernMessageCard from './TavernMessageCard';
import ChatComposer from './ChatComposer';
import { replayableMatches } from '../utils/arena/matchHistory';
import { getFeed as getMomentsFeed, sendMomentToFriend } from '../utils/moments/momentsManager';
import { isWeb } from '../utils/platform';
import { t } from '../localization/t';

// Bubbles sit on the panel's carved stone, so they are plates with a
// hairline rim and a shallow drop rather than frosted glass. The blur is
// gone with the translucency: the panel base is opaque now, so each
// bubble was paying for a backdrop filter that had nothing to sample.
const BUBBLE_RADIUS = 'var(--radius-lg)';
const SENT_BUBBLE = {
  background: 'rgba(180, 140, 60, 0.11)',
  boxShadow: [
    'inset 0 0 0 1px rgba(180, 140, 60, 0.24)',
    'inset 0 1px 0 rgba(255, 235, 195, 0.06)',
    '0 1px 2px rgba(0, 0, 0, 0.35)',
  ].join(', '),
};
const RECV_BUBBLE = {
  background: 'rgba(255, 255, 255, 0.045)',
  boxShadow: [
    'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
    'inset 0 1px 0 rgba(255, 255, 255, 0.04)',
    '0 1px 2px rgba(0, 0, 0, 0.35)',
  ].join(', '),
};
// The social hub insets its text by its panel padding (0.7cqw) plus its
// row padding (0.625cqw). The chat panel has no panel padding of its own,
// because the message list wants the width, so its content carries the
// whole inset itself. Text lands on the same vertical line as the hub
// docked beside it.
const EDGE_PADDING = '1.325cqw';
const AVATAR_SIZE = '2cqw';
const AVATAR_RING = 'inset 0 0 0 1px rgba(255,255,255,0.08)';
const PANEL_RING = '#151107';
// Floating menu ladder, matching the audio mixer popover:
//   menu 0.7cqw ─ padding 0.35cqw ─→ item 0.35cqw
const MENU_RADIUS = 'var(--radius-xl)';
const MENU_PADDING = '0.375cqw';
const MENU_ITEM_RADIUS = 'var(--radius-md)';

// Attachments carry the collection identity (card, art group, foiling), so
// the art group picks the illustration. Otherwise an alt-art attachment
// previews as the booster art the sender isn't actually sending.
function resolveCardImage(cardId, artGroup, sorceryCards) {
  if (!cardId || !Array.isArray(sorceryCards)) return null;
  for (const card of sorceryCards) {
    if (card.unique_id === cardId) {
      const printing = printingsInArtGroup(card, artGroup || ART_GROUP_BASE)[0]
        || card.printings?.[0];
      if (printing?.image_url) return printing.image_url;
    }
  }
  return null;
}

function resolveCardName(cardId, artGroup, sorceryCards) {
  if (!cardId || !Array.isArray(sorceryCards)) return cardId;
  for (const card of sorceryCards) {
    if (card.unique_id === cardId) {
      return artGroupDisplayName(card, artGroup || ART_GROUP_BASE) || cardId;
    }
  }
  return cardId;
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default class ChatView extends Component {
  constructor(props) {
    super(props);
    const cached = getCachedMessages(props.profile?.id, props.friendId);
    this.state = {
      messages: cached,
      loading: cached.length === 0,
      loadingOlder: false,
      hasOlder: true,
      inputText: '',
      sending: false,
      // Attachment state
      showAttachMenu: false,
      showGoldInput: false,
      attachCards: [],   // [{ cardId, artGroup, foiling }]
      attachCoins: 0,
      hoveredCard: null,
      inspectedCard: null,
      mentionPreview: null,
      selectedCards: {}, // { messageId: Set<cardIndex> }
      // Tracks whether the friend we're chatting with has a resolved
      // crown title. PlayerTitle resolves asynchronously, so we wait
      // for its onResolve callback before appending the trailing
      // comma to the friend's name in the header.
      friendHasTitle: false,
    };
    this.scrollRef = createRef();
    this.bottomRef = createRef();
    this.inspectorHold = new InspectorHoldTracker();
  }

  persistMessages = (messages) => {
    saveCachedMessages(this.props.profile?.id, this.props.friendId, messages);
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleInspectorKey);
    window.addEventListener('keyup', this.handleInspectorKeyUp);
    this.loadMessages();
  }

  componentDidUpdate(_prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedCard &&
      this.state.hoveredCard &&
      this.state.hoveredCard !== prevState.hoveredCard &&
      this.inspectorHold.consumePending()
    ) {
      playUI(UI.INSPECTOR_OPEN);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedCard: this.state.hoveredCard });
    }

    const inspectorOpen = this.state.inspectedCard != null;
    const wasInspectorOpen = prevState.inspectedCard != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => {
        playUI(UI.INSPECTOR_CLOSE);
        this.setState({ inspectedCard: null });
      });
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleInspectorKey);
    window.removeEventListener('keyup', this.handleInspectorKeyUp);
    removeDismiss(this._escDismissId);
    if (this._momentUrlCache) {
      for (const url of this._momentUrlCache.values()) {
        try { URL.revokeObjectURL(url); } catch {}
      }
      this._momentUrlCache.clear();
    }
  }

  handleInspectorKey = (e) => {
    if (e.repeat) return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
    if (e.target?.isContentEditable) return;
    if (e.key === ' ' || e.code === 'Space') {
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) {
        if (this.state.inspectedCard || this.state.hoveredCard) e.preventDefault();
        return;
      }
      if (this.state.inspectedCard) {
        e.preventDefault();
        playUI(UI.INSPECTOR_CLOSE);
        this.inspectorHold.noteClose();
        this.setState({ inspectedCard: null });
      } else if (this.state.hoveredCard) {
        e.preventDefault();
        playUI(UI.INSPECTOR_OPEN);
        this.inspectorHold.noteOpen();
        this.setState({ inspectedCard: this.state.hoveredCard });
      } else {
        // No card hovered yet. Arm so the next hover opens.
        e.preventDefault();
        this.inspectorHold.noteArmed();
      }
    }
  };

  // Open the card inspector for a @-mention click in a message body.
  handleOpenMentionCard = (cardId) => {
    const sorceryCards = this.props.sorceryCards;
    const full = sorceryCards?.find((c) => c.unique_id === cardId);
    if (!full) return;
    playUI(UI.INSPECTOR_OPEN);
    this.setState({
      inspectedCard: {
        card: full,
        printing: full.printings?.[0],
        rarity: full.rarity,
      },
    });
  };

  handleMentionHover = (cardId, element) => {
    const sorceryCards = this.props.sorceryCards;
    const full = sorceryCards?.find((c) => c.unique_id === cardId);
    if (!full || !element?.getBoundingClientRect) return;
    const rect = rectToCanvasRect(element.getBoundingClientRect());
    this.setState({ mentionPreview: { card: full, rect } });
  };

  handleMentionHoverEnd = () => {
    if (this.state.mentionPreview) this.setState({ mentionPreview: null });
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleInspectorKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedCard) return;
    e.preventDefault();
    playUI(UI.INSPECTOR_CLOSE);
    this.setState({ inspectedCard: null });
  };

  loadMessages = async () => {
    try {
      const fresh = (await fetchMessages(this.props.friendId)).reverse();
      this.setState(
        (s) => {
          const merged = mergeMessages(s.messages, fresh);
          this.persistMessages(merged);
          return { messages: merged, loading: false };
        },
        () => {
          this.scrollToBottom();
          this.markAsRead();
        }
      );
    } catch (err) {
      console.error('[ChatView] Failed to load messages:', err);
      this.setState({ loading: false });
    }
  };

  loadOlderMessages = async () => {
    const { messages, loadingOlder, hasOlder } = this.state;
    if (loadingOlder || !hasOlder || messages.length === 0) return;
    this.setState({ loadingOlder: true });
    try {
      const older = await fetchMessages(this.props.friendId, { before: messages[0].id });
      if (older.length === 0) {
        this.setState({ hasOlder: false, loadingOlder: false });
        return;
      }
      this.setState((s) => {
        const merged = mergeMessages(s.messages, older.reverse());
        this.persistMessages(merged);
        return {
          messages: merged,
          loadingOlder: false,
          hasOlder: older.length >= 50,
        };
      });
    } catch {
      this.setState({ loadingOlder: false });
    }
  };

  markAsRead = () => {
    const { messages } = this.state;
    if (messages.length === 0) return;
    const lastId = messages[messages.length - 1].id;
    // Refresh the Mailbox badge summary so the Social tab + header
    // drop their unread count as soon as the read cursor advances.
    // without waiting for the next presence poll.
    markChatRead(this.props.friendId, lastId)
      .then(() => refreshMailbox())
      .catch(() => {});
  };

  scrollToBottom = () => {
    const container = this.scrollRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  };

  handleScroll = (e) => {
    if (e.target.scrollTop < 60) this.loadOlderMessages();
  };

  handleSend = async () => {
    const { inputText, sending, attachCards, attachCoins } = this.state;
    const text = inputText.trim();
    const hasAttachments = attachCards.length > 0 || attachCoins > 0;
    if ((!text && !hasAttachments) || sending) return;

    // Aggregate by the full collection identity. Two arts of one card in one
    // finish are separate rows on the server and must be sent as separate
    // attachments, or the consume drains whichever row the default names.
    const cardMap = new Map();
    for (const entry of attachCards) {
      const key = `${entry.cardId}:${entry.artGroup}:${entry.foiling}`;
      const existing = cardMap.get(key);
      if (existing) existing.quantity++;
      else cardMap.set(key, { cardId: entry.cardId, artGroup: entry.artGroup, foiling: entry.foiling, quantity: 1 });
    }
    const cards = [...cardMap.values()];

    const optimistic = {
      id: `pending-${Date.now()}`,
      senderId: this.props.profile?.id,
      recipientId: this.props.friendId,
      type: hasAttachments ? 'attachment' : 'text',
      body: text || null,
      attachedCards: cards,
      attachedCoins: attachCoins,
      claimed: false,
      metadata: null,
      createdAt: new Date().toISOString(),
      _pending: true,
    };

    this.setState(
      (s) => ({
        messages: [...s.messages, optimistic],
        inputText: '',
        attachCards: [],
        attachCoins: 0,
        showAttachMenu: false,
        showGoldInput: false,
        sending: true,
      }),
      this.scrollToBottom
    );

    try {
      const msg = await sendChatMessage(this.props.friendId, {
        body: text || undefined,
        cards: cards.length > 0 ? cards : undefined,
        coins: attachCoins > 0 ? attachCoins : undefined,
      });
      this.setState((s) => {
        const next = s.messages.map((m) => (m.id === optimistic.id ? msg : m));
        this.persistMessages(next);
        return { messages: next, sending: false };
      });
      if (hasAttachments && this.props.onProfileReload) this.props.onProfileReload();
    } catch (err) {
      this.setState((s) => ({
        messages: s.messages.map((m) =>
          m.id === optimistic.id ? { ...m, _error: err.message || 'Failed to send' } : m
        ),
        sending: false,
      }));
    }
  };

  handleClaim = async (messageId, selectedIndices = null) => {
    const msg = this.state.messages.find((m) => m.id === messageId);
    const coinsInMsg = msg?.attachedCoins || 0;
    const hadCards = Array.isArray(msg?.attachedCards) && msg.attachedCards.length > 0;
    try {
      const result = await claimChatMessage(messageId, selectedIndices);
      this.setState((s) => {
        const next = s.messages.map((m) => {
          if (m.id !== messageId) return m;
          if (!selectedIndices) return { ...m, claimed: true };
          // Partial claim. Remove selected cards from the message
          const remaining = (m.attachedCards || []).filter((_, idx) => !selectedIndices.includes(idx));
          return {
            ...m,
            attachedCards: remaining,
            attachedCoins: 0, // coins always claimed
            claimed: remaining.length === 0,
          };
        });
        this.persistMessages(next);
        return { messages: next };
      });
      // Coin-only messages don't change the collection, so a lightweight
      // profile patch keeps the chat mounted. Card claims still need the
      // full reload to pick up collection changes.
      if (hadCards) {
        if (this.props.onProfileReload) this.props.onProfileReload();
      } else if (coinsInMsg > 0 && this.props.onProfileUpdate) {
        const newCoins = typeof result?.coins === 'number'
          ? result.coins
          : (this.props.profile?.coins || 0) + coinsInMsg;
        this.props.onProfileUpdate({ coins: newCoins });
      }
    } catch (err) {
      console.error('[ChatView] Claim failed:', err);
    }
  };

  toggleCardSelection = (messageId, cardIndex) => {
    this.setState((s) => {
      const key = `${messageId}`;
      const current = s.selectedCards?.[key] || new Set();
      const next = new Set(current);
      if (next.has(cardIndex)) next.delete(cardIndex);
      else next.add(cardIndex);
      return { selectedCards: { ...s.selectedCards, [key]: next } };
    });
  };

  receiveMessage = (msg) => {
    this.setState(
      (s) => {
        const next = mergeMessages(s.messages, [msg]);
        this.persistMessages(next);
        return { messages: next };
      },
      () => { this.scrollToBottom(); this.markAsRead(); }
    );
  };

  handleClaimed = (data) => {
    this.setState((s) => {
      const next = s.messages.map((m) => (m.id === data.messageId ? { ...m, claimed: true } : m));
      this.persistMessages(next);
      return { messages: next };
    });
  };

  addCard = (cardId, artGroup, foiling) => {
    this.setState((s) => {
      if (s.attachCards.length >= 10) return null;
      return {
        attachCards: [...s.attachCards, {
          cardId, artGroup: artGroup || ART_GROUP_BASE, foiling: foiling || 'S',
        }],
      };
    });
  };

  removeCard = (index) => {
    this.setState((s) => {
      const next = [...s.attachCards];
      next.splice(index, 1);
      return { attachCards: next };
    });
  };

  // `paymentMethod` ('keystone' | 'coins' | null) comes off the card's pay
  // picker and rides through onAcceptDraftInvite into openDraftLobby's join
  // call, the same route the Notifications menu's Accept takes.
  handleDraftInviteAccept = (msg, paymentMethod = null) => {
    const eventId = msg.metadata?.eventId;
    if (!eventId) return;
    this.setState((s) => {
      const next = s.messages.map((m) => (m.id === msg.id ? { ...m, claimed: 'accepted' } : m));
      this.persistMessages(next);
      return { messages: next };
    });
    claimChatMessage(msg.id).catch(() => {});
    this.props.onAcceptDraftInvite?.(eventId, paymentMethod ? { paymentMethod } : undefined);
  };

  handleDraftInviteDecline = (msg) => {
    const eventId = msg.metadata?.eventId;
    this.setState((s) => {
      const next = s.messages.map((m) => (m.id === msg.id ? { ...m, claimed: 'declined' } : m));
      this.persistMessages(next);
      return { messages: next };
    });
    claimChatMessage(msg.id).catch(() => {});
    // Best-effort: tell the server so the creator gets refunded when
    // this was the last pending invite. Local state is already updated;
    // failures here don't undo the user-visible decline.
    if (eventId) declineDraftEvent(eventId).catch(() => {});
  };

  // Turn a base64 moment payload into a cached object URL. Same
  // message on subsequent renders returns the same URL so we don't
  // allocate + revoke every paint. Cache is tied to this component
  // instance and cleaned up in componentWillUnmount.
  _momentUrlFor(msg) {
    if (!this._momentUrlCache) this._momentUrlCache = new Map();
    const cached = this._momentUrlCache.get(msg.id);
    if (cached) return cached;
    const webp = msg?.metadata?.webp;
    if (typeof webp !== 'string' || !webp) return null;
    try {
      const binary = atob(webp);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'image/webp' });
      const url = URL.createObjectURL(blob);
      this._momentUrlCache.set(msg.id, url);
      return url;
    } catch {
      return null;
    }
  }

  renderMomentAttachment(msg) {
    const url = this._momentUrlFor(msg);
    if (!url) return null;
    return (
      <button
        type="button"
        className="mt-1 block rounded-lg overflow-hidden cursor-zoom-in"
        style={{
          background: 'rgba(14,12,8,0.55)',
          border: `1px solid ${GOLD} 0.22)`,
          maxWidth: '17.5cqw',
        }}
        onClick={() => this.setState({ momentLightboxUrl: url })}
      >
        <img
          src={url}
          alt=""
          style={{
            display: 'block',
            width: '100%',
            maxWidth: '17.5cqw',
            maxHeight: '12.5cqw',
            objectFit: 'cover',
          }}
        />
      </button>
    );
  }

  renderMessage = (msg) => {
    const isMine = msg.senderId === this.props.profile?.id;
    const hasCards = Array.isArray(msg.attachedCards) && msg.attachedCards.length > 0;
    const hasCoins = (msg.attachedCoins || 0) > 0;
    const hasAttachments = hasCards || hasCoins;
    const canClaim = !isMine && hasAttachments && !msg.claimed;
    const isDraftInvite = msg.type === 'draft-invite';
    const isMoment = msg.type === 'moment';
    const isReplay = msg.type === 'replay';
    // A forward the server snapshotted. One sent through a server that did
    // not know the type yet carries only the fallback body, and reads as
    // plain text below.
    const isTavern = msg.type === 'tavern' && !!msg.metadata?.body;
    const sorceryCards = this.props.sorceryCards;

    if (isTavern) {
      return (
        <div
          key={msg.id}
          className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`}
        >
          <div className="w-[88%]" style={{ opacity: msg._pending ? 0.6 : 1 }}>
            <TavernMessageCard
              meta={msg.metadata}
              sorceryCards={sorceryCards}
              onOpen={this.props.onOpenTavernMessage}
            />
            <div
              className="text-[0.5625cqw] mt-1"
              style={{ color: TEXT_MUTED, textAlign: isMine ? 'right' : 'left' }}
            >
              {formatTime(msg.createdAt)}
            </div>
          </div>
        </div>
      );
    }

    // A replay carries no body text, no cards and no coins, so the bubble
    // around it would only ever be a second rounded rectangle wrapping the
    // card's own. It renders as the bubble instead.
    if (isReplay) {
      return (
        <div
          key={msg.id}
          className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`}
        >
          <div className="w-[88%]" style={{ opacity: msg._pending ? 0.6 : 1 }}>
            <ReplayShareCard
              meta={msg.metadata || {}}
              sorceryCards={sorceryCards}
              onWatch={this.props.onWatchReplay}
            />
            <div
              className="text-[0.5625cqw] mt-1"
              style={{ color: TEXT_MUTED, textAlign: isMine ? 'right' : 'left' }}
            >
              {formatTime(msg.createdAt)}
            </div>
          </div>
        </div>
      );
    }

    // Same treatment as a replay, for the same reason: the invite card is
    // already a plate with its own ground, radius and shadow. A bubble
    // around it made three nested rounded rectangles out of one invite.
    if (isDraftInvite && msg.metadata) {
      return (
        <div
          key={msg.id}
          className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`}
        >
          <div className="w-[88%]" style={{ opacity: msg._pending ? 0.6 : 1 }}>
            <DraftInviteCard
              meta={msg.metadata}
              mine={isMine}
              senderName={this.props.friendName}
              senderAvatarId={this.props.friendAvatar}
              recipientName={this.props.friendName}
              status={msg.claimed}
              coins={this.props.profile?.coins || 0}
              keystones={this.props.profile?.keystones || 0}
              sorceryCards={sorceryCards}
              onAccept={(paymentMethod) => this.handleDraftInviteAccept(msg, paymentMethod)}
              onDecline={() => this.handleDraftInviteDecline(msg)}
            />
            <div
              className="text-[0.5625cqw] mt-1"
              style={{ color: TEXT_MUTED, textAlign: isMine ? 'right' : 'left' }}
            >
              {formatTime(msg.createdAt)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={msg.id}
        className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`}
      >
        <div
          className="max-w-[75%] px-3 py-2"
          style={{
            borderRadius: BUBBLE_RADIUS,
            ...(isMine ? SENT_BUBBLE : RECV_BUBBLE),
            opacity: msg._pending ? 0.6 : 1,
          }}
        >
          {msg.body && (
            <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: TEXT_BODY }}>
              <CardMentionText
                text={msg.body}
                onOpenCard={this.handleOpenMentionCard}
                onCardHover={this.handleMentionHover}
                onCardHoverEnd={this.handleMentionHoverEnd}
              />
            </div>
          )}

          {isMoment ? this.renderMomentAttachment(msg) : null}

          {hasCards && (() => {
            const selected = this.state.selectedCards?.[msg.id] || new Set();
            const isCollected = msg.claimed;
            return (
              <>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {msg.attachedCards.map((card, i) => {
                    const imgUrl = resolveCardImage(card.cardId, card.artGroup, sorceryCards);
                    const name = resolveCardName(card.cardId, card.artGroup, sorceryCards);
                    const isSelected = selected.has(i);
                    return (
                      <div
                        key={`${card.cardId}-${i}`}
                        className="relative rounded-card overflow-hidden cursor-pointer transition-all hover:scale-[1.03]"
                        style={{
                          width: '4.25cqw',
                          border: isSelected ? `2px solid ${ACCENT_GOLD}` : `1.5px solid ${isCollected ? 'rgba(255,255,255,0.06)' : `${GOLD} 0.2)`}`,
                          boxShadow: isSelected ? `0 0 10px rgba(212,168,67,0.3)` : isCollected ? 'none' : '0 2px 8px rgba(0,0,0,0.3)',
                          opacity: isCollected ? 0.35 : 1,
                        }}
                        title={`${name}${card.quantity > 1 ? ` x${card.quantity}` : ''}`}
                        onClick={() => { if (canClaim) this.toggleCardSelection(msg.id, i); }}
                        onMouseEnter={() => {
                          const fullCard = sorceryCards?.find(c => c.unique_id === card.cardId);
                          if (fullCard) this.setState({ hoveredCard: { card: fullCard, printing: fullCard.printings?.[0], rarity: fullCard.rarity } });
                        }}
                        onMouseLeave={() => this.setState({ hoveredCard: null })}
                      >
                        {imgUrl ? (
                          <img src={imgUrl} alt={name} className="w-full aspect-[63/88] object-cover" draggable={false} />
                        ) : (
                          <div className="w-full aspect-[63/88] flex items-center justify-center text-[0.5cqw] text-center px-0.5" style={{ background: `${GOLD} 0.04)`, color: TEXT_MUTED }}>
                            {name}
                          </div>
                        )}
                        {card.quantity > 1 && (
                          <div className="absolute top-0.5 right-0.5 text-[0.5625cqw] font-bold px-1 rounded" style={{ background: 'rgba(0,0,0,0.75)', color: TEXT_PRIMARY }}>
                            x{card.quantity}
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
                            <span style={{ color: ACCENT_GOLD, fontSize: '1cqw' }}>&#x2714;</span>
                          </div>
                        )}
                        {isCollected && (
                          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
                            <span style={{ color: TEXT_MUTED, fontSize: '0.875cqw' }}>&#x2714;</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {canClaim && selected.size > 0 && (
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      className="vh-invite-btn is-primary"
                      data-sound={UI.CONFIRM}
                      onClick={() => this.handleClaim(msg.id, [...selected])}
                    >
                      {selected.size === msg.attachedCards.length ? t('Collect All') : selected.size === 1 ? t('Collect 1 Card') : t('Collect {n} Cards', { n: selected.size })}
                    </button>
                    <button
                      type="button"
                      className="vh-invite-btn"
                      onClick={() => this.setState((s) => ({ selectedCards: { ...s.selectedCards, [msg.id]: new Set() } }))}
                    >
                      {t('Clear')}
                    </button>
                  </div>
                )}
                {canClaim && selected.size === 0 && (
                  <div className="text-[0.5625cqw] mt-1.5" style={{ color: TEXT_MUTED }}>
                    {t('Tap cards to select, then collect')}
                  </div>
                )}
              </>
            );
          })()}

          {hasCoins && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <div className="flex items-center gap-1">
                <CoinIcon size={12} />
                <span className="text-[0.6875cqw] font-medium" style={{ color: COIN_COLOR, opacity: msg.claimed ? 0.35 : 1 }}>
                  {msg.attachedCoins} {msg.claimed ? t('(collected)') : ''}
                </span>
              </div>
              {canClaim && !hasCards && (
                <button
                  type="button"
                  className="vh-invite-btn is-primary"
                  data-sound={UI.CONFIRM}
                  onClick={() => this.handleClaim(msg.id)}
                >
                  {t('Collect Gold')}
                </button>
              )}
            </div>
          )}

          {hasAttachments && msg.claimed && (
            <div className="text-[0.5625cqw] mt-1.5 flex items-center gap-1" style={{ color: TEXT_MUTED }}>
              <span style={{ color: ACCENT_GOLD }}>&#x2714;</span> {t('Collected')}
            </div>
          )}

          {/* Match invites are no longer delivered through chat. They
              live in the Notifications menu, backed by server-side
              instance invitations. Draft invites leave before this
              point: they render as their own card, not in a bubble. */}

          {msg._error && (
            <div className="text-[0.625cqw] mt-1" style={{ color: '#e89090' }}>{t('Failed to send')}</div>
          )}

          <div className="text-[0.5625cqw] mt-1" style={{ color: TEXT_MUTED, textAlign: isMine ? 'right' : 'left' }}>
            {formatTime(msg.createdAt)}
          </div>
        </div>
      </div>
    );
  };

  renderAttachMenu() {
    return (
      <div
        className="absolute bottom-full left-2.5 mb-2 z-20 flex flex-col"
        style={{
          ...OPAQUE_TEXTURED_PANEL_STYLE,
          borderRadius: MENU_RADIUS,
          padding: MENU_PADDING,
          minWidth: '10cqw',
        }}
      >
        <button
          type="button"
          className="vh-chat-menu-item w-full flex items-center gap-2.5 px-2.5 py-2 text-xs cursor-pointer"
          style={{ borderRadius: MENU_ITEM_RADIUS, color: TEXT_PRIMARY }}
          onClick={() => {
            this.setState({ showAttachMenu: false });
            this.props.onOpenCardPicker?.();
          }}
        >
          <svg style={{ width: '0.9375cqw', height: '0.9375cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 15l6-6 4 4 4-4 4 4" />
          </svg>
          {t('Send Cards')}
        </button>
        <button
          type="button"
          className="vh-chat-menu-item w-full flex items-center gap-2.5 px-2.5 py-2 text-xs cursor-pointer"
          style={{ borderRadius: MENU_ITEM_RADIUS, color: COIN_COLOR }}
          onClick={() => this.setState({ showAttachMenu: false, showGoldInput: true })}
        >
          <CoinIcon size={15} />
          {t('Send Gold')}
        </button>
        {isWeb() ? null : (
        <button
          type="button"
          className="vh-chat-menu-item w-full flex items-center gap-2.5 px-2.5 py-2 text-xs cursor-pointer"
          style={{ borderRadius: MENU_ITEM_RADIUS, color: TEXT_PRIMARY }}
          onClick={() => this.setState({ showAttachMenu: false, showMomentPicker: true })}
        >
          <svg style={{ width: '0.9375cqw', height: '0.9375cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 7h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          {t('Send Moment')}
        </button>
        )}
        <button
          type="button"
          className="vh-chat-menu-item w-full flex items-center gap-2.5 px-2.5 py-2 text-xs cursor-pointer"
          style={{ borderRadius: MENU_ITEM_RADIUS, color: TEXT_PRIMARY }}
          onClick={() => this.setState({ showAttachMenu: false, showReplayPicker: true })}
        >
          <svg style={{ width: '0.9375cqw', height: '0.9375cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M10 8.5 15.5 12 10 15.5z" fill="currentColor" stroke="none" />
          </svg>
          {t('Send Replay')}
        </button>
      </div>
    );
  }

  // Finished matches with an archived replay; picking one shares watch
  // access with this friend and drops a replay card into the chat.
  renderReplayPicker() {
    const { showReplayPicker, replayPickerSending } = this.state;
    if (!showReplayPicker) return null;
    const matches = replayableMatches(this.props.matchHistory);
    return (
      <div
        className="absolute z-40 overflow-hidden flex flex-col"
        style={{
          bottom: '100%',
          left: 0,
          right: 0,
          marginBottom: 6,
          ...OPAQUE_TEXTURED_PANEL_STYLE,
          borderRadius: MENU_RADIUS,
          maxHeight: '16.25cqw',
        }}
      >
        <div className="shrink-0 flex items-center justify-between px-3 py-2" style={{ borderBottom: `1px solid ${GOLD} 0.1)` }}>
          <span className="text-[0.6875cqw] font-semibold" style={{ color: ACCENT_GOLD }}>
            {t('Send a Replay')}
          </span>
          <CloseButton
            title={t('Close')}
            onClick={() => this.setState({ showReplayPicker: false })}
            style={{ width: '1.5cqw', height: '1.5cqw' }}
            size={12}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-1.5">
          {matches.length === 0 ? (
            <div className="text-[0.6875cqw] text-center py-6" style={{ color: TEXT_MUTED }}>
              {t('No watchable matches yet.')}
            </div>
          ) : matches.map((m, i) => (
            <button
              key={`${m.roomCode}-${i}`}
              type="button"
              disabled={replayPickerSending}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-all hover:bg-white/5 text-left"
              style={{ opacity: replayPickerSending ? 0.4 : 1 }}
              onClick={() => this.handleSendReplay(m)}
            >
              <span
                className="inline-flex items-center justify-center text-[0.5625cqw] font-bold shrink-0"
                style={{
                  width: '1.25cqw', height: '1.25cqw', borderRadius: '50%',
                  background: m.won ? 'rgba(122, 208, 106, 0.18)' : 'rgba(224, 112, 96, 0.18)',
                  border: `1px solid ${m.won ? 'rgba(122, 208, 106, 0.55)' : 'rgba(224, 112, 96, 0.55)'}`,
                  color: m.won ? '#7ad06a' : '#e07060',
                }}
              >
                {m.won ? 'W' : 'L'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[0.6875cqw] truncate" style={{ color: TEXT_PRIMARY }}>{m.customName || t('vs {name}', { name: m.opponentName || t('Opponent') })}</span>
                <span className="block text-[0.5625cqw] truncate" style={{ color: TEXT_MUTED }}>{[m.customName ? t('vs {name}', { name: m.opponentName || t('Opponent') }) : null, m.playedAt ? new Date(m.playedAt).toLocaleDateString() : null].filter(Boolean).join(' \u00b7 ')}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  handleSendReplay = async (m) => {
    const { friendId } = this.props;
    if (!friendId || !m?.roomCode || this.state.replayPickerSending) return;
    this.setState({ replayPickerSending: true });
    try {
      // Both seats' Avatar cards, from the archived bundle. A solo match
      // writes ONE match_history row and no avatar_id, so this is the only
      // place the pilot's two decks are knowable, and the card needs them to
      // show a matchup instead of the same name twice. Best-effort: a card
      // without it falls back to the seat-suffix labels.
      const summary = await fetchReplaySummary(m.roomCode).catch(() => null);
      const res = await sendChatMessage(friendId, {
        type: 'replay',
        // Snapshotted, not looked up: match_history is one row per
        // participant, so the sender's label, the mode and both Avatar ids
        // live on rows the recipient cannot read. Sender first; `won` on
        // each seat is what the card reads to mark the winner.
        metadata: {
          ...replayMetaFromHistory(m, this.props.profile),
          matchAvatars: summary?.avatars || null,
          // The archive's own word on solo, so the card does not have to
          // infer it from the pilot's name sitting in both seats.
          solo: !!summary?.solo,
        },
      });
      const message = res?.message || res;
      if (message?.id) {
        this.setState((s) => ({
          messages: Array.isArray(s.messages) ? [...s.messages, message] : [message],
        }));
      }
      this.setState({ showReplayPicker: false });
    } catch (err) {
      console.error('[chat] send replay failed:', err);
    } finally {
      this.setState({ replayPickerSending: false });
    }
  };

  renderMomentPicker() {
    const { showMomentPicker, momentPickerSending } = this.state;
    if (!showMomentPicker) return null;
    const feed = getMomentsFeed().filter((m) => m.kind === 'self');
    return (
      <div
        className="absolute z-40 overflow-hidden flex flex-col"
        style={{
          bottom: '100%',
          left: 0,
          right: 0,
          marginBottom: 6,
          ...OPAQUE_TEXTURED_PANEL_STYLE,
          borderRadius: MENU_RADIUS,
          maxHeight: '16.25cqw',
        }}
      >
        <div className="shrink-0 flex items-center justify-between px-3 py-2" style={{ borderBottom: `1px solid ${GOLD} 0.1)` }}>
          <span className="text-[0.6875cqw] font-semibold" style={{ color: ACCENT_GOLD }}>
            {t('Send a Moment')}
          </span>
          <CloseButton
            title={t('Close')}
            onClick={() => this.setState({ showMomentPicker: false })}
            style={{ width: '1.5cqw', height: '1.5cqw' }}
            size={12}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {feed.length === 0 ? (
            <div className="text-[0.6875cqw] text-center py-6" style={{ color: TEXT_MUTED }}>
              {t('No moments yet. Capture one from the Moments panel first.')}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {feed.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  disabled={momentPickerSending}
                  onClick={() => this.handleSendMoment(m)}
                  className="rounded-md overflow-hidden cursor-pointer"
                  style={{
                    aspectRatio: '16 / 9',
                    border: `1px solid ${GOLD} 0.18)`,
                    opacity: momentPickerSending ? 0.4 : 1,
                  }}
                  onMouseEnter={(e) => { if (!momentPickerSending) e.currentTarget.style.borderColor = `${GOLD} 0.45)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.18)`; }}
                >
                  <img src={m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  handleSendMoment = async (entry) => {
    const { friendId } = this.props;
    if (!friendId || !entry || this.state.momentPickerSending) return;
    this.setState({ momentPickerSending: true });
    try {
      const message = await sendMomentToFriend(friendId, entry);
      // Optimistically append the new message so it shows up
      // immediately. The real chat:message WS push will replace/
      // dedupe it when it echoes back.
      if (message) {
        this.setState((s) => ({
          messages: Array.isArray(s.messages) ? [...s.messages, message] : [message],
        }));
      }
    } catch (err) {
      console.error('[ChatView] send moment failed:', err);
    } finally {
      this.setState({ momentPickerSending: false, showMomentPicker: false });
    }
  };

  renderGoldInput() {
    const maxCoins = this.props.profile?.coins || 0;
    const { attachCoins } = this.state;
    return (
      <div className="shrink-0 py-1.5 flex items-center gap-2" style={{ paddingLeft: '0.7cqw', paddingRight: '0.7cqw' }}>
        <CoinIcon size={14} />
        <input
          type="number"
          min={0}
          max={maxCoins}
          value={attachCoins || ''}
          placeholder={t('Enter amount')}
          className="flex-1 px-3 py-1.5 text-xs leading-5 outline-none vh-chat-input"
          style={{
            background: OPAQUE_SUB_PANEL_STYLE.background,
            backgroundBlendMode: OPAQUE_SUB_PANEL_STYLE.backgroundBlendMode,
            borderRadius: 'var(--radius-3xl)',
            color: COIN_COLOR,
            height: '2cqw',
          }}
          onInput={(e) => {
            const val = Math.max(0, Math.min(maxCoins, parseInt(e.target.value, 10) || 0));
            this.setState({ attachCoins: val });
          }}
        />
        <span className="text-[0.625cqw] shrink-0" style={{ color: TEXT_MUTED }}>/ {maxCoins}</span>
        <button
          type="button"
          className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer shrink-0"
          style={{ color: TEXT_MUTED, background: 'rgba(255,255,255,0.04)' }}
          onClick={() => this.setState({ showGoldInput: false, attachCoins: 0 })}
        >
          <svg style={{ width: '0.625cqw', height: '0.625cqw' }} viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>
    );
  }

  render() {
    const { friendName, friendAvatar, online, onBack, sorceryCards } = this.props;
    const avatarUrl = resolveAvatarUrl({ profileAvatar: friendAvatar }, sorceryCards);
    const { messages, loading, loadingOlder, inputText, showAttachMenu, showGoldInput, attachCards, attachCoins } = this.state;
    const hasAttachments = attachCards.length > 0 || attachCoins > 0;
    const canSend = inputText.trim() || hasAttachments;

    return (
      <div className="relative flex flex-col h-full">
        {/* Header. Avatar+name on the left, close chevron on the
            right (rotated 180° so it points toward the right edge,
            cueing "this closes the chat" as a directional gesture). */}
        <div
          className="flex items-center gap-2.5 py-2 shrink-0"
          style={{ paddingLeft: EDGE_PADDING, paddingRight: panelCornerGutter() }}
        >
          <div
            className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer"
            onClick={this.props.onHeaderClick || undefined}
            title={this.props.onHeaderClick ? t("View {name}'s profile", { name: friendName }) : undefined}
          >
            <div className="relative">
              {avatarUrl ? (
                <AvatarImage src={avatarUrl} alt={friendName} className="rounded-full" style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, boxShadow: AVATAR_RING }} />
              ) : (
                <div
                  className="rounded-full flex items-center justify-center text-[0.6875cqw] font-semibold"
                  style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, background: `${GOLD} 0.14)`, boxShadow: AVATAR_RING, color: TEXT_PRIMARY }}
                >
                  {(friendName || '?')[0].toUpperCase()}
                </div>
              )}
              {online && (
                <div
                  className="absolute -bottom-0.5 -right-0.5 rounded-full"
                  style={{ width: '0.5625cqw', height: '0.5625cqw', background: '#4ade80', boxShadow: `0 0 0 2px ${PANEL_RING}` }}
                />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold" style={{ color: TEXT_PRIMARY }}>
                {friendName}{this.state.friendHasTitle ? ',' : ''}
              </div>
              {/* Crown title. Mirrors the comment-section pattern:
                  comma trailing the name, italic title on its own row
                  below. PlayerTitle drives the comma flag via its
                  onResolve callback. */}
              <PlayerTitle
                player={{
                  id: this.props.friendId,
                  displayedCrownAvatarId: this.props.friendDisplayedCrownAvatarId,
                  isFounder: !!this.props.friendIsFounder,
                  isQaTester: !!this.props.friendIsQaTester,
                }}
                sorceryCards={sorceryCards}
                prefix=""
                style={{ fontSize: '0.55cqw', display: 'block', lineHeight: 1.1, marginTop: '-0.15cqw', color: TEXT_PRIMARY, whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                onResolve={(has) => {
                  if (has !== this.state.friendHasTitle) {
                    this.setState({ friendHasTitle: has });
                  }
                }}
              />
              <div className="text-[0.5625cqw]" style={{ color: online ? '#4ade80' : TEXT_MUTED, marginTop: '0.1cqw' }}>
                {online ? t('Online') : t('Offline')}
              </div>
            </div>
          </div>
          {/* Pinned to the panel's corner, not centred in this row: the
              header is three lines tall, so a row-centred button had a top
              inset set by the text beside it rather than by the panel. */}
          <ForwardButton ghost title={t('Close chat')} onClick={onBack} style={panelCornerStyle()} size={14} />
        </div>

        {/* Messages */}
        <div
          ref={this.scrollRef}
          className="flex-1 overflow-y-auto py-2 min-h-0"
          style={{ paddingLeft: EDGE_PADDING, paddingRight: EDGE_PADDING }}
          onScroll={this.handleScroll}
        >
          {loadingOlder && (
            <div className="flex justify-center py-2"><RuneSpinner size={16} /></div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12"><RuneSpinner size={28} /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xs" style={{ color: TEXT_MUTED }}>{t('No messages yet')}</p>
              <p className="text-[0.625cqw] mt-1" style={{ color: TEXT_MUTED }}>{t('Say hello to {name}!', { name: friendName })}</p>
            </div>
          ) : (
            messages.map((msg) => this.renderMessage(msg))
          )}
          <div ref={this.bottomRef} />
        </div>

        {/* Attachment preview. Floating above input, overlapping chat */}
        {hasAttachments && (
          <div className="relative shrink-0" style={{ zIndex: 5 }}>
            <div
              className="absolute bottom-0 left-0 right-0 pb-2 pt-3 flex gap-2.5 overflow-x-auto"
              style={{ scrollbarWidth: 'none', paddingLeft: EDGE_PADDING, paddingRight: EDGE_PADDING }}
            >
              {attachCards.map((card, i) => {
                const imgUrl = resolveCardImage(card.cardId, card.artGroup, this.props.sorceryCards);
                const name = resolveCardName(card.cardId, card.artGroup, this.props.sorceryCards);
                return (
                  <div key={i} className="relative shrink-0">
                    <div
                      className="rounded-xl"
                      style={{
                        overflow: 'hidden',
                        width: '4.5cqw',
                        height: Math.round(72 * 88 / 63),
                        border: `1.5px solid ${GOLD} 0.3)`,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)',
                      }}
                    >
                      {imgUrl ? (
                        <img src={imgUrl} alt={name} className="w-full h-full object-cover" draggable={false} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[0.5cqw] text-center px-1" style={{ background: `${GOLD} 0.06)`, color: TEXT_MUTED }}>{name}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center cursor-pointer"
                      style={{ background: '#b04040', color: '#fff', fontSize: '0.6875cqw', fontWeight: 700, border: '2px solid #0e0a06' }}
                      onClick={() => this.removeCard(i)}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
              {attachCoins > 0 && (
                <div className="flex items-center gap-1.5 px-3 rounded-xl shrink-0 self-end" style={{ height: '2.25cqw', background: `${GOLD} 0.1)`, border: `1.5px solid ${GOLD} 0.25)`, boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}>
                  <CoinIcon size={16} />
                  <span className="text-sm font-bold" style={{ color: COIN_COLOR }}>{attachCoins}</span>
                </div>
              )}
            </div>
            {/* Spacer to push input bar down. Half the card height so cards overlap chat */}
            <div style={{ height: Math.round(72 * 88 / 63) / 2 + 16 }} />
          </div>
        )}

        {/* Gold amount input */}
        {showGoldInput && this.renderGoldInput()}

        {/* Composer. One control: the frame IS the input and both buttons
            live inside it, the way the deck-notes CommentComposer does. */}
        <ChatComposer
          value={inputText}
          onValueChange={(next) => this.setState({ inputText: next })}
          onSubmit={this.handleSend}
          canSend={!!canSend}
          attachOpen={showAttachMenu}
          onToggleAttach={() => this.setState((st) => ({ showAttachMenu: !st.showAttachMenu, showGoldInput: false }))}
          sorceryCards={this.props.sorceryCards}
          onOpenCard={this.handleOpenMentionCard}
          onCardHover={this.handleMentionHover}
          onCardHoverEnd={this.handleMentionHoverEnd}
        >
          {showAttachMenu && this.renderAttachMenu()}
          {this.renderMomentPicker()}
          {this.renderReplayPicker()}
          {/* Backdrop to close menu or picker */}
          {(showAttachMenu || this.state.showMomentPicker) && (
            <div
              className="fixed inset-0 z-10"
              onClick={() => this.setState({ showAttachMenu: false, showMomentPicker: false })}
            />
          )}
        </ChatComposer>

        {/* Floating card preview for @-mention hovers in message bodies. */}
        {this.state.mentionPreview ? (
          <CardHoverPreview
            card={this.state.mentionPreview.card}
            rect={this.state.mentionPreview.rect}
          />
        ) : null}

        {/* Card Inspector */}
        {this.state.inspectedCard && (
          <CardInspector
            card={this.state.inspectedCard.card}
            imageUrl={this.state.inspectedCard.printing?.image_url}
            rarity={this.state.inspectedCard.rarity}
            foiling={this.state.inspectedCard.printing?.foiling}
            onClose={() => { playUI(UI.INSPECTOR_CLOSE); this.setState({ inspectedCard: null }); }}
          />
        )}

        {this.state.momentLightboxUrl ? createPortal(
          // Portal to document.body so the fixed positioning escapes
          // every ancestor stacking context. The Mailbox + ChatPanel
          // chain applies `zoom: var(--viewport-scale)` and its own
          // transforms, which scope fixed children to the panel, not
          // the viewport. Without the portal the lightbox sits
          // UNDER the Moments panel (z-74) and centers against the
          // chat pane instead of the screen. z-[200] puts it above
          // every other overlay in the app.
          <div
            data-screenshot-ignore="true"
            className="fixed inset-0 z-[200] flex items-center justify-center cursor-zoom-out"
            style={{ background: 'rgba(0,0,0,0.86)', backdropFilter: 'blur(8px)' }}
            onClick={() => this.setState({ momentLightboxUrl: null })}
          >
            <img
              src={this.state.momentLightboxUrl}
              alt=""
              style={{
                maxWidth: '90vw',
                maxHeight: '88vh',
                width: 'auto',
                height: 'auto',
                borderRadius: 'var(--radius-xl)',
                border: `1px solid ${GOLD} 0.35)`,
                boxShadow: '0 20px 80px rgba(0,0,0,0.8)',
              }}
            />
          </div>,
          document.body,
        ) : null}
      </div>
    );
  }
}
